import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Title, Text, Badge, Button, Flex, TabGroup, TabList, Tab, TabPanels, TabPanel, Select, SelectItem } from '@tremor/react';
import { Tooltip } from '../components/Tooltip';
import { ResultsTextPanel } from '../components/ResultsTextPanel';
import type { ResultLabel } from '../components/ResultsTextPanel';
import { api } from '../lib/api';
import type { WorkflowResponse, MsgPreviewResponse, DocxPreviewResponse } from '../types';

// MIME types with no page coordinates — analyzed text-only, no native browser
// preview. .msg and .docx are rendered to HTML server-side instead.
const MSG_MIME = 'application/vnd.ms-outlook';
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DOC_MIME = 'application/msword'; // legacy binary Word — previewed as text (word-extractor)

// Helper to group sentences by label
type HarmfulResult = {
  sentenceIndex: number;
  sentence: string;
  labels: Array<{
    classifier: string;
    classifierName?: string;
    label: string;
    backend?: string;
  }>;
};

type SentenceBbox = {
  sentenceIndex: number;
  text?: string;
  bbox?: {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    page: number;  // 1-indexed page number
  };
};

type ExtractionResult = {
  page: number;
  confidence: number;
  source: 'embedded' | 'ocr' | 'error';
  sentences: string[];
};

function groupSentencesByLabel(
  harmfulResults: HarmfulResult[] | undefined,
  sentenceBboxes: SentenceBbox[] | undefined,
  extractionResults: ExtractionResult[] | undefined
) {
  const grouped: Record<string, Array<{
    sentenceIndex: number;
    sentence: string;
    classifierName?: string;
    label: string;
    backend?: string;     // 'openai' or 'phi'
    pageNumber?: number;  // 1-indexed for display
    confidence?: number;  // 0-100 extraction confidence
    source?: 'embedded' | 'ocr' | 'error';
  }>> = {};

  if (!harmfulResults) return grouped;

  // Build map from sentenceIndex to page number
  // bbox.page is already 1-indexed (directly from PDF pageNum)
  const pageMap = new Map<number, number>();
  if (sentenceBboxes) {
    for (const bbox of sentenceBboxes) {
      if (bbox.sentenceIndex !== undefined && bbox.bbox?.page !== undefined) {
        pageMap.set(bbox.sentenceIndex, bbox.bbox.page);
      }
    }
  }

  // Build map from page number to confidence/source
  const confidenceMap = new Map<number, { confidence: number; source: 'embedded' | 'ocr' | 'error' }>();
  if (extractionResults) {
    for (const result of extractionResults) {
      confidenceMap.set(result.page, { confidence: result.confidence, source: result.source });
    }
  }

  for (const result of harmfulResults) {
    for (const labelInfo of result.labels) {
      // Use composite key for grouping: classifierName:label
      const key = labelInfo.classifierName
        ? `${labelInfo.classifierName}:${labelInfo.label}`
        : labelInfo.label;

      if (!grouped[key]) {
        grouped[key] = [];
      }
      // Avoid duplicate sentences for the same label
      if (!grouped[key].some(s => s.sentenceIndex === result.sentenceIndex)) {
        const pageNumber = pageMap.get(result.sentenceIndex);
        const pageConfidence = pageNumber ? confidenceMap.get(pageNumber) : undefined;

        grouped[key].push({
          sentenceIndex: result.sentenceIndex,
          sentence: result.sentence,
          classifierName: labelInfo.classifierName,
          label: labelInfo.label,
          backend: labelInfo.backend,
          pageNumber,
          confidence: pageConfidence?.confidence,
          source: pageConfidence?.source,
        });
      }
    }
  }

  // Sort sentences by page number first, then by sentence index
  for (const key of Object.keys(grouped)) {
    grouped[key].sort((a, b) => {
      // Sort by page first (items without page go last)
      if (a.pageNumber !== undefined && b.pageNumber !== undefined) {
        if (a.pageNumber !== b.pageNumber) return a.pageNumber - b.pageNumber;
      } else if (a.pageNumber !== undefined) {
        return -1;  // a has page, b doesn't - a comes first
      } else if (b.pageNumber !== undefined) {
        return 1;   // b has page, a doesn't - b comes first
      }
      // Then by sentence index
      return a.sentenceIndex - b.sentenceIndex;
    });
  }

  return grouped;
}

function escapeCsvValue(value: string | number | undefined): string {
  if (value === undefined || value === null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function exportLabelsCsv(
  documentId: string,
  harmfulResults: HarmfulResult[] | undefined,
  sentenceBboxes: SentenceBbox[] | undefined,
  extractionResults: ExtractionResult[] | undefined,
  displayFileName?: string
) {
  if (!harmfulResults || harmfulResults.length === 0) return;

  const baseName = (() => {
    const raw = (displayFileName ?? '').replace(/\.[^./\\]+$/, '');
    const safe = raw.replace(/[<>:"/\\|?*\x00-\x1f]/g, '-').trim().replace(/\.+$/, '');
    return safe || documentId;
  })();

  // Build page map from bboxes
  const pageMap = new Map<number, number>();
  if (sentenceBboxes) {
    for (const bbox of sentenceBboxes) {
      if (bbox.sentenceIndex !== undefined && bbox.bbox?.page !== undefined) {
        pageMap.set(bbox.sentenceIndex, bbox.bbox.page);
      }
    }
  }

  // Build confidence map from extraction results
  const confidenceMap = new Map<number, { confidence: number; source: string }>();
  if (extractionResults) {
    for (const result of extractionResults) {
      confidenceMap.set(result.page, { confidence: result.confidence, source: result.source });
    }
  }

  const columns = ['document_id', 'classifier', 'label', 'page_number', 'sentence_index', 'sentence', 'backend', 'confidence'];
  const csvRows: string[] = [columns.join(',')];

  // One row per (sentence, label) pair — iterate raw harmfulResults
  const allEntries: Array<{
    classifierName: string;
    label: string;
    pageNumber?: number;
    sentenceIndex: number;
    sentence: string;
    backend?: string;
    confidence?: number;
  }> = [];

  for (const result of harmfulResults) {
    const pageNumber = pageMap.get(result.sentenceIndex);
    const pageConf = pageNumber ? confidenceMap.get(pageNumber) : undefined;

    for (const labelInfo of result.labels) {
      allEntries.push({
        classifierName: labelInfo.classifierName || labelInfo.classifier,
        label: labelInfo.label,
        pageNumber,
        sentenceIndex: result.sentenceIndex,
        sentence: result.sentence,
        backend: labelInfo.backend,
        confidence: pageConf?.confidence,
      });
    }
  }

  // Sort by page number, then sentence index
  allEntries.sort((a, b) => {
    const pa = a.pageNumber ?? Infinity;
    const pb = b.pageNumber ?? Infinity;
    if (pa !== pb) return pa - pb;
    return a.sentenceIndex - b.sentenceIndex;
  });

  for (const entry of allEntries) {
    csvRows.push([
      escapeCsvValue(documentId),
      escapeCsvValue(entry.classifierName),
      escapeCsvValue(entry.label),
      escapeCsvValue(entry.pageNumber),
      escapeCsvValue(entry.sentenceIndex + 1),
      escapeCsvValue(entry.sentence),
      escapeCsvValue(entry.backend),
      escapeCsvValue(entry.confidence !== undefined ? `${entry.confidence}%` : undefined),
    ].join(','));
  }

  const csv = csvRows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${baseName}-labels.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '--';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(ms: number | null | undefined): string {
  if (!ms) return '--';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

function getStatusColor(status: string): 'emerald' | 'blue' | 'red' | 'yellow' | 'gray' {
  switch (status) {
    case 'completed': return 'emerald';
    case 'processing': return 'blue';
    case 'failed': return 'red';
    case 'pending': return 'yellow';
    default: return 'gray';
  }
}

const GENRE_OPTIONS = [
  { label: 'Other', displayName: 'Other' },
  { label: 'Government_Reports_&_Records', displayName: 'Government Reports & Records' },
  { label: 'Legislative_&_Ordinance_Documents', displayName: 'Legislative & Ordinance Documents' },
  { label: 'Legal,_Corporate_&_Property_Filings', displayName: 'Legal, Corporate & Property Filings' },
  { label: 'Email', displayName: 'Email' },
  { label: 'Blog_/_Website_Post', displayName: 'Blog / Website Post' },
  { label: 'News_Story', displayName: 'News Story' },
  { label: 'Transcript', displayName: 'Transcript' },
  { label: 'Financial_&_Tabular_Materials', displayName: 'Financial & Tabular Materials' },
  { label: 'Visuals:_Maps,_Drawings,_Diagrams,_Photos', displayName: 'Visuals: Maps, Drawings, Diagrams, Photos' },
  { label: 'Program_&_Promotional_Materials', displayName: 'Program & Promotional Materials' },
  { label: 'Community_Advocacy_Materials', displayName: 'Community Advocacy Materials' },
  { label: 'Texts_&_Messaging', displayName: 'Texts & Messaging' },
  { label: 'Social_Media_Post', displayName: 'Social Media Post' },
  { label: 'Petitions,_Surveys_&_Polling', displayName: 'Petitions, Surveys & Polling' },
];

export function DocumentDetailPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState<WorkflowResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileUrlError, setFileUrlError] = useState<string | null>(null);
  const [annotatedFileUrl, setAnnotatedFileUrl] = useState<string | null>(null);
  const [annotatedFileUrlError, setAnnotatedFileUrlError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [hasAutoSelectedTab, setHasAutoSelectedTab] = useState(false);
  const [expandedLabels, setExpandedLabels] = useState<Set<string>>(new Set());
  const [labelFilter, setLabelFilter] = useState<'all' | 'toAnnotate' | 'notAnnotated'>('toAnnotate');
  const [msgPreview, setMsgPreview] = useState<MsgPreviewResponse | null>(null);
  const [msgPreviewError, setMsgPreviewError] = useState<string | null>(null);
  const [docxPreview, setDocxPreview] = useState<DocxPreviewResponse | null>(null);
  const [docxPreviewError, setDocxPreviewError] = useState<string | null>(null);
  const [isEditingGenre, setIsEditingGenre] = useState(false);
  const [genreSaving, setGenreSaving] = useState(false);
  const [showExtractedText, setShowExtractedText] = useState(false);
  // Active flagged sentence in the Results tab (shared with the Labels Found panel
  // so clicking a sentence there can jump to it). Null = nothing focused yet.
  const [activeSentenceIndex, setActiveSentenceIndex] = useState<number | null>(null);

  // Navigation between documents
  const navInfo = useMemo(() => {
    try {
      const listJson = sessionStorage.getItem('documentNavList');
      if (!listJson || !documentId) return null;

      const list: string[] = JSON.parse(listJson);
      const currentIndex = list.indexOf(documentId);

      if (currentIndex === -1) return null;

      return {
        list,
        currentIndex,
        prevId: currentIndex > 0 ? list[currentIndex - 1] : null,
        nextId: currentIndex < list.length - 1 ? list[currentIndex + 1] : null,
        position: `${currentIndex + 1} of ${list.length}`,
      };
    } catch {
      return null;
    }
  }, [documentId]);

  const navigateTo = (id: string) => {
    if (navInfo) {
      sessionStorage.setItem('documentNavIndex', String(navInfo.list.indexOf(id)));
    }
    navigate(`/documents/${encodeURIComponent(id)}`);
  };

  // Fetch workflow data
  useEffect(() => {
    if (!documentId) return;

    setIsLoading(true);
    setError(null);

    api.getWorkflow(documentId)
      .then((response) => {
        setWorkflow(response);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load document');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [documentId]);

  // Fetch signed file URL when workflow is loaded and file is available
  // MSG/DOCX/DOC documents are excluded — they have a server-rendered HTML preview
  // and fetching the file URL here would make the browser download it instead.
  useEffect(() => {
    if (!workflow || !documentId) return;
    const mime = workflow.fetchResult?.mimeType;
    if (mime === MSG_MIME || mime === DOCX_MIME || mime === DOC_MIME) return;

    const hasFile = !!workflow.fetchResult?.fileName ||
                    !!workflow.fetchResult?.targetFileName ||
                    workflow.status === 'completed' ||
                    ['extract', 'evaluate', 'annotate', 'tag', 'completed'].includes(workflow.currentStep);

    if (!hasFile) {
      setFileUrl(null);
      return;
    }

    // Fetch original file URL
    api.getDocumentFileUrl(documentId, 'original')
      .then((response) => {
        setFileUrl(response.url);
        setFileUrlError(null);
      })
      .catch((err) => {
        setFileUrlError(err instanceof Error ? err.message : 'Failed to get file URL');
        setFileUrl(null);
      });

    // Fetch annotated file URL if available
    const hasAnnotatedFile = !!workflow.annotationResult?.annotatedFileName;
    if (hasAnnotatedFile) {
      api.getDocumentFileUrl(documentId, 'annotated')
        .then((response) => {
          setAnnotatedFileUrl(response.url);
          setAnnotatedFileUrlError(null);
        })
        .catch((err) => {
          setAnnotatedFileUrlError(err instanceof Error ? err.message : 'Failed to get annotated file URL');
          setAnnotatedFileUrl(null);
        });
    } else {
      setAnnotatedFileUrl(null);
    }
  }, [workflow, documentId]);

  // Fetch MSG preview for .msg documents — gets its own signed URL, does not trigger a download
  useEffect(() => {
    if (!documentId || !workflow || workflow.fetchResult?.mimeType !== MSG_MIME) return;
    const hasFile = !!workflow.fetchResult?.fileName || workflow.status === 'completed' ||
                    ['extract', 'evaluate', 'annotate', 'tag', 'completed'].includes(workflow.currentStep);
    if (!hasFile) return;
    api.getDocumentFileUrl(documentId, 'original')
      .then((response) => api.getMsgPreview(documentId, response.url))
      .then((data) => { setMsgPreview(data); setMsgPreviewError(null); })
      .catch((err) => setMsgPreviewError(err instanceof Error ? err.message : 'Failed to load message preview'));
  }, [documentId, workflow?.fetchResult?.mimeType, workflow?.status]);

  // Fetch Word preview for .docx (mammoth → HTML) and .doc (word-extractor → text)
  // documents — rendered server-side. Like MSG, gets its own signed URL and does
  // not trigger a download.
  useEffect(() => {
    if (!documentId || !workflow) return;
    const mime = workflow.fetchResult?.mimeType;
    if (mime !== DOCX_MIME && mime !== DOC_MIME) return;
    const hasFile = !!workflow.fetchResult?.fileName || workflow.status === 'completed' ||
                    ['extract', 'evaluate', 'annotate', 'tag', 'completed'].includes(workflow.currentStep);
    if (!hasFile) return;
    api.getDocumentFileUrl(documentId, 'original')
      .then((response) => api.getDocxPreview(documentId, response.url))
      .then((data) => { setDocxPreview(data); setDocxPreviewError(null); })
      .catch((err) => setDocxPreviewError(err instanceof Error ? err.message : 'Failed to load document preview'));
  }, [documentId, workflow?.fetchResult?.mimeType, workflow?.status]);

  // One-time default tab selection once data is available. Prefer Results
  // (extracted text + highlighted labels); fall back to Annotated, then Original.
  // Guarded so it never overrides a manual tab change.
  useEffect(() => {
    if (hasAutoSelectedTab) return;
    if (workflow?.extractResult?.sentences?.length) {
      setSelectedTab(2); // Results
      setHasAutoSelectedTab(true);
    } else if (annotatedFileUrl) {
      setSelectedTab(1); // Annotated
      setHasAutoSelectedTab(true);
    }
  }, [workflow?.extractResult?.sentences?.length, annotatedFileUrl, hasAutoSelectedTab]);

  // Group sentences by label for expandable view (must be before early returns)
  const sentencesByLabel = useMemo(
    () => groupSentencesByLabel(
      workflow?.evaluationResult?.harmfulResults,
      workflow?.extractResult?.sentenceBboxes,
      workflow?.extractResult?.extractionResults
    ),
    [workflow?.evaluationResult?.harmfulResults, workflow?.extractResult?.sentenceBboxes, workflow?.extractResult?.extractionResults]
  );

  // For the Results tab: sentenceIndex -> labels found on it, and the sorted list
  // of flagged sentence indices (the Next/Prev stop list). Built straight from the
  // flat harmfulResults so indices match the flat `sentences` array exactly.
  const { harmfulSentenceMap, harmfulIndices } = useMemo(() => {
    const map = new Map<number, ResultLabel[]>();
    const harmfulResults = workflow?.evaluationResult?.harmfulResults;
    if (harmfulResults) {
      for (const result of harmfulResults) {
        const existing = map.get(result.sentenceIndex) ?? [];
        for (const l of result.labels) {
          // De-dupe identical classifierName:label pairs on the same sentence
          const dupe = existing.some(
            (e) => e.classifierName === l.classifierName && e.label === l.label
          );
          if (!dupe) {
            existing.push({ classifierName: l.classifierName, label: l.label, backend: l.backend });
          }
        }
        map.set(result.sentenceIndex, existing);
      }
    }
    const indices = Array.from(map.keys()).sort((a, b) => a - b);
    return { harmfulSentenceMap: map, harmfulIndices: indices };
  }, [workflow?.evaluationResult?.harmfulResults]);

  // Jump from the Labels Found panel (or anywhere) to a flagged sentence in Results.
  const RESULTS_TAB = 2;
  const jumpToResult = (sentenceIndex: number) => {
    setActiveSentenceIndex(sentenceIndex);
    setSelectedTab(RESULTS_TAB);
  };

  // Compute "to annotate" labels (those not in excludedLabels) - independent of filter state
  const toAnnotateSentencesByLabel = useMemo(() => {
    const excludedLabels = workflow?.annotationResult?.annotationCoverage?.excludedLabels || [];
    if (excludedLabels.length === 0) {
      return sentencesByLabel;
    }
    // excludedLabels contains full composite keys (classifierName:label)
    // sentencesByLabel keys are also in the same format
    return Object.fromEntries(
      Object.entries(sentencesByLabel).filter(([compositeKey]) => {
        return !excludedLabels.includes(compositeKey);
      })
    );
  }, [sentencesByLabel, workflow?.annotationResult?.annotationCoverage?.excludedLabels]);

  // Compute "not annotated" labels (those IN excludedLabels) - opposite of toAnnotate
  const notAnnotatedSentencesByLabel = useMemo(() => {
    const excludedLabels = workflow?.annotationResult?.annotationCoverage?.excludedLabels || [];
    if (excludedLabels.length === 0) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(sentencesByLabel).filter(([compositeKey]) => {
        return excludedLabels.includes(compositeKey);
      })
    );
  }, [sentencesByLabel, workflow?.annotationResult?.annotationCoverage?.excludedLabels]);

  // Filter sentencesByLabel based on label filter toggle
  const filteredSentencesByLabel = useMemo(() => {
    if (labelFilter === 'all') {
      return sentencesByLabel;
    }
    if (labelFilter === 'notAnnotated') {
      return notAnnotatedSentencesByLabel;
    }
    return toAnnotateSentencesByLabel;
  }, [sentencesByLabel, toAnnotateSentencesByLabel, notAnnotatedSentencesByLabel, labelFilter]);

  // Toggle label expansion
  const toggleLabel = (label: string) => {
    setExpandedLabels(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-[1600px] mx-auto">
        <Card className="bg-gray-900 ring-gray-800">
          <div className="flex items-center justify-center py-12">
            <Text className="text-gray-500">Loading document...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !workflow) {
    return (
      <div className="max-w-[1600px] mx-auto">
        <Card className="bg-gray-900 ring-gray-800">
          <div className="text-center py-12">
            <Text className="text-red-400 mb-4">{error || 'Document not found'}</Text>
            <Button
              variant="secondary"
              onClick={() => navigate('/documents')}
              className="bg-gray-800 border-gray-700"
            >
              Back to Documents
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Extract data from workflow using the correct field names
  const fileName = workflow.fetchResult?.displayFileName || workflow.fetchResult?.fileName || workflow.fetchResult?.targetFileName || workflow.documentId;
  const fileSize = workflow.fetchResult?.fileSize;
  const pageCount = workflow.extractResult?.extractedPagesCount || workflow.extractResult?.pageCount;
  const sentenceCount = workflow.extractResult?.sentenceCount || workflow.evaluationResult?.sentenceCount;
  const labelCounts = workflow.evaluationResult?.labelCounts;

  // Check if file should be available (for UI messaging)
  const hasFile = !!workflow.fetchResult?.fileName ||
                  !!workflow.fetchResult?.targetFileName ||
                  workflow.status === 'completed' ||
                  ['extract', 'evaluate', 'annotate', 'tag', 'completed'].includes(workflow.currentStep);

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <Card className="bg-gray-900 ring-gray-800">
        <Flex justifyContent="between" alignItems="center">
          <div className="flex items-center gap-4">
            <Link
              to="/documents"
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              &larr; Back to Documents
            </Link>
            {(workflow.metadata as Record<string, any>)?.batchMetadata?.batchName && (
              <>
                <span className="text-gray-600">|</span>
                <Link
                  to={`/documents?batch=${encodeURIComponent((workflow.metadata as Record<string, any>).batchMetadata.batchName)}`}
                  className="text-gray-500 hover:text-gray-300 text-sm transition-colors truncate max-w-xs"
                  title={(workflow.metadata as Record<string, any>).batchMetadata.batchName}
                >
                  {(workflow.metadata as Record<string, any>).batchMetadata.batchName}
                </Link>
              </>
            )}
          </div>
          {navInfo && (
            <div className="flex items-center gap-2">
              <Button
                size="xs"
                variant="secondary"
                disabled={!navInfo.prevId}
                onClick={() => navInfo.prevId && navigateTo(navInfo.prevId)}
                className="bg-gray-800 border-gray-700 px-2"
              >
                &larr; Prev
              </Button>
              <Text className="text-gray-500 text-xs">{navInfo.position}</Text>
              <Button
                size="xs"
                variant="secondary"
                disabled={!navInfo.nextId}
                onClick={() => navInfo.nextId && navigateTo(navInfo.nextId)}
                className="bg-gray-800 border-gray-700 px-2"
              >
                Next &rarr;
              </Button>
            </div>
          )}
        </Flex>
      </Card>

      {/* Skipped Document Alert */}
      {workflow.annotationResult?.skipped && (
        <Card className="bg-amber-950/30 ring-amber-900/50 border border-amber-800">
          <div className="flex items-start gap-3">
            <span className="text-amber-400 text-xl">⚠️</span>
            <div>
              <Text className="text-amber-400 font-semibold mb-1">Document Skipped</Text>
              <Text className="text-gray-300">
                {workflow.annotationResult.skipReason || 'This document was skipped during processing.'}
              </Text>
              {workflow.annotationResult.skipCode && (
                <Text className="text-gray-500 text-sm mt-1">
                  Code: {workflow.annotationResult.skipCode}
                </Text>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Main content - two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Details */}
        <div className="space-y-6">
          {/* Results (formerly Tag Results) - Show after annotation is complete */}
          {workflow.tagResult && !workflow.tagResult.skipped && (
            <Card className="bg-gray-900 ring-gray-800">
              <Title className="text-gray-100 mb-4">Results</Title>
              <div className="space-y-3">
                {/* Upload status */}
                <div className="flex justify-between items-center">
                  <Text className="text-gray-400">PDF Upload</Text>
                  {workflow.tagResult.uploadSuccess ? (
                    <Badge color="emerald" size="sm">Success</Badge>
                  ) : workflow.tagResult.uploadSkipped ? (
                    <Badge color="gray" size="sm" title="No folders to assign - upload skipped">Skipped</Badge>
                  ) : workflow.tagResult.uploadError ? (
                    <Badge color="red" size="sm" title={workflow.tagResult.uploadError}>Failed</Badge>
                  ) : (
                    <Badge color="gray" size="sm">Not uploaded</Badge>
                  )}
                </div>

                {/* Folders assigned */}
                <div className="flex justify-between items-center">
                  <Text className="text-gray-400">Folders Assigned</Text>
                  <Text className="text-gray-200">
                    {workflow.tagResult.foldersAssigned ?? 0}
                    {(workflow.tagResult.foldersFailed ?? 0) > 0 && (
                      <span className="text-red-400 ml-1">
                        ({workflow.tagResult.foldersFailed} failed)
                      </span>
                    )}
                  </Text>
                </div>

                {/* Show assigned folders */}
                {workflow.tagResult.assignedFolders && workflow.tagResult.assignedFolders.length > 0 && (
                  <div className="pt-2">
                    <Text className="text-gray-500 text-sm mb-2">Assigned to:</Text>
                    <div className="flex flex-wrap gap-1">
                      {workflow.tagResult.assignedFolders.map((folder) => (
                        <Badge key={folder.id} color="emerald" size="xs">
                          {folder.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show failed folders */}
                {workflow.tagResult.failedFolders && workflow.tagResult.failedFolders.length > 0 && (
                  <div className="pt-2">
                    <Text className="text-red-400 text-sm mb-2">Failed assignments:</Text>
                    <div className="space-y-1">
                      {workflow.tagResult.failedFolders.map((folder) => (
                        <div key={folder.id} className="flex justify-between items-center bg-red-950/30 rounded p-2">
                          <Text className="text-gray-300 text-sm">{folder.name}</Text>
                          <Text className="text-red-400 text-xs">{folder.error}</Text>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Labels info */}
                {workflow.tagResult.labelsProcessed && workflow.tagResult.labelsProcessed.length > 0 && (
                  <div className="pt-2 border-t border-gray-800">
                    <Text className="text-gray-500 text-sm mb-2">Labels processed:</Text>
                    <div className="flex flex-wrap gap-1">
                      {workflow.tagResult.labelsProcessed.map((label) => (
                        <Badge key={label} color="blue" size="xs">{label}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {workflow.tagResult.labelsSkipped && workflow.tagResult.labelsSkipped.length > 0 && (
                  <div className="pt-2">
                    <Text className="text-gray-500 text-sm mb-2">Labels skipped (no folder mapping):</Text>
                    <div className="flex flex-wrap gap-1">
                      {workflow.tagResult.labelsSkipped.map((label) => (
                        <Badge key={label} color="yellow" size="xs">{label}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Processing time - total across all steps */}
                {workflow.metrics && (
                  <div className="flex justify-between pt-2 border-t border-gray-800">
                    <Text className="text-gray-500 text-sm">Processing time</Text>
                    <Text className="text-gray-400 text-sm">
                      {(() => {
                        const m = workflow.metrics;
                        const sumOfSteps = (m.fetchTime || 0) + (m.extractTime || 0) +
                          (m.evaluateTime || 0) + (m.annotateTime || 0) + (m.tagTime || 0);
                        return formatDuration(sumOfSteps > 0 ? sumOfSteps : m.totalProcessingTime);
                      })()}
                    </Text>
                  </div>
                )}

                {/* View in Xera - at bottom of Results panel */}
                {workflow.sourceConfig?.projectId && (
                  <div className="pt-3 border-t border-gray-800">
                    <a
                      href={`https://xera.cloud.relmanlaw.com/projects/${workflow.sourceConfig.projectId}/records/${workflow.metadata?.xeraRecordId || workflow.documentId}?cv=0`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-full bg-blue-900/30 border-blue-700 hover:bg-blue-900/50 text-blue-300"
                      >
                        View in iConect ↗
                      </Button>
                    </a>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Document Info */}
          <Card className="bg-gray-900 ring-gray-800">
            <Title className="text-gray-100 mb-4">Document Info</Title>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Text className="text-gray-400">Status</Text>
                {workflow.annotationResult?.skipped ? (
                  <Badge color="orange" size="sm">Skipped</Badge>
                ) : (
                  <Badge color={getStatusColor(workflow.status)} size="sm">{workflow.status}</Badge>
                )}
              </div>
              <InfoRow label="Document ID" value={workflow.documentId} />
              <div className="flex justify-between items-center">
                <Text className="text-gray-400">Filename</Text>
                <div className="flex items-center gap-2">
                  <Text className="text-gray-200">{fileName || '--'}</Text>
                  {(fileUrl || workflow.fetchResult?.mimeType === MSG_MIME || workflow.fetchResult?.mimeType === DOCX_MIME || workflow.fetchResult?.mimeType === DOC_MIME) && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const url = fileUrl || (await api.getDocumentFileUrl(documentId!, 'original')).url;
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = fileName || documentId!;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        } catch {}
                      }}
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                      title="Download file"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <InfoRow label="File Size" value={formatFileSize(fileSize)} />
              {/* Show original file type if alternate PDF source was used, otherwise show mimeType */}
              <InfoRow
                label="File Type"
                value={workflow.fetchResult?.originalFileType || workflow.fetchResult?.mimeType || '--'}
              />
              {workflow.fetchResult?.usedAlternateField && (
                <div className="flex justify-between items-center">
                  <Text className="text-gray-400">Source</Text>
                  <Badge color="blue" size="xs">Alternate PDF</Badge>
                </div>
              )}
              {workflow.extractResult?.isEncrypted && (
                <div className="flex justify-between items-center">
                  <Text className="text-gray-400">PDF Security</Text>
                  <Badge color="yellow" size="xs">Permission Restricted</Badge>
                </div>
              )}
              <InfoRow label="Pages" value={pageCount?.toString() || '--'} />
              <InfoRow label="Sentences" value={sentenceCount?.toString() || '--'} />
              {/* Genre classification with override support */}
              <div className="flex justify-between items-center">
                <Text className="text-gray-400">Genre</Text>
                <div className="flex items-center gap-2">
                  {workflow.genreOverrideDisplayName ? (
                    <>
                      {workflow.evaluationResult?.genreDisplayName && (
                        <span className="line-through text-gray-500 text-xs">
                          {workflow.evaluationResult.genreDisplayName}
                        </span>
                      )}
                      <Badge
                        color="amber"
                        size="sm"
                        title={`Overridden ${workflow.genreOverrideAt ? new Date(workflow.genreOverrideAt).toLocaleDateString() : ''}`}
                      >
                        {workflow.genreOverrideDisplayName}
                      </Badge>
                    </>
                  ) : workflow.evaluationResult?.genreDisplayName ? (
                    <Badge
                      color={['empty', 'error', 'unknown'].includes(workflow.evaluationResult?.genreLabel || '') ? 'gray' : 'indigo'}
                      size="sm"
                    >
                      {workflow.evaluationResult.genreDisplayName}
                    </Badge>
                  ) : (
                    <Text className="text-gray-500">--</Text>
                  )}
                  {!isEditingGenre && (
                    <button
                      onClick={() => setIsEditingGenre(true)}
                      className="text-gray-500 hover:text-blue-400 transition-colors"
                      title="Override genre"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              {isEditingGenre && (
                <div className="flex items-center gap-2 bg-gray-800/50 rounded p-2">
                  <Select
                    value=""
                    onValueChange={async (value) => {
                      if (!documentId) return;
                      setGenreSaving(true);
                      try {
                        const result = await api.overrideGenre(documentId, value);
                        setWorkflow(prev => prev ? {
                          ...prev,
                          genreOverrideLabel: result.genreOverrideLabel,
                          genreOverrideDisplayName: result.genreOverrideDisplayName,
                          genreOverrideAt: result.genreOverrideAt,
                        } : prev);
                        setIsEditingGenre(false);
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Failed to override genre');
                      } finally {
                        setGenreSaving(false);
                      }
                    }}
                    placeholder="Select genre..."
                    disabled={genreSaving}
                    className="flex-1"
                  >
                    {GENRE_OPTIONS.map(({ label, displayName }) => (
                      <SelectItem key={label} value={label}>{displayName}</SelectItem>
                    ))}
                  </Select>
                  <button
                    onClick={() => setIsEditingGenre(false)}
                    className="text-gray-400 hover:text-gray-200 text-sm"
                    disabled={genreSaving}
                  >
                    Cancel
                  </button>
                </div>
              )}
              {/* Extraction confidence from OCR/embedded text */}
              {(() => {
                const results = workflow.extractResult?.extractionResults;
                if (!results || results.length === 0) return null;

                const ocrPages = results.filter(r => r.source === 'ocr');
                const embeddedPages = results.filter(r => r.source === 'embedded');
                const errorPages = results.filter(r => r.source === 'error');

                // Calculate average confidence (weighted by source)
                const avgConfidence = results.length > 0
                  ? Math.round(results.reduce((sum, r) => sum + (r.confidence || 0), 0) / results.length)
                  : null;

                const confidenceColor = avgConfidence === null ? 'gray'
                  : avgConfidence >= 90 ? 'emerald'
                  : avgConfidence >= 70 ? 'yellow'
                  : 'red';

                return (
                  <>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <Text className="text-gray-400">Text Source</Text>
                        <Tooltip text="How text was extracted from each page. 'Embedded' means the PDF had selectable text. 'OCR' means optical character recognition was used on scanned images." />
                      </div>
                      <div className="flex gap-1">
                        {embeddedPages.length > 0 && (
                          <Badge color="emerald" size="xs">
                            {embeddedPages.length} embedded
                          </Badge>
                        )}
                        {ocrPages.length > 0 && (
                          <Badge color="blue" size="xs">
                            {ocrPages.length} OCR
                          </Badge>
                        )}
                        {errorPages.length > 0 && (
                          <Badge color="red" size="xs">
                            {errorPages.length} failed
                          </Badge>
                        )}
                      </div>
                    </div>
                    {avgConfidence !== null && (
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <Text className="text-gray-400">Extraction Confidence</Text>
                          <Tooltip text="Average confidence score across all pages. Embedded text is 100% confident. OCR confidence depends on image quality. Low confidence may indicate unreliable text extraction." />
                        </div>
                        <Badge color={confidenceColor} size="xs">
                          {avgConfidence}%
                        </Badge>
                      </div>
                    )}
                  </>
                );
              })()}
              {/* Annotation method - only show for non-standard cases */}
              {workflow.annotationResult?.annotationMethod === 'skipped' && (
                <div className="flex justify-between items-center">
                  <Text className="text-gray-400">Annotation</Text>
                  <div className="flex items-center gap-2">
                    <Badge color="red" size="xs">Skipped</Badge>
                    {workflow.annotationResult.annotationMethodDetail && (
                      <Tooltip text={workflow.annotationResult.annotationMethodDetail} />
                    )}
                  </div>
                </div>
              )}
              {workflow.annotationResult?.annotationMethod === 'qpdf-decrypt' && (
                <div className="flex justify-between items-center">
                  <Text className="text-gray-400">Annotation</Text>
                  <div className="flex items-center gap-2">
                    <Badge color="yellow" size="xs">Decrypted PDF</Badge>
                    <Tooltip text="PDF had permission restrictions. It was decrypted with qpdf before annotation — verify highlights are correct." />
                  </div>
                </div>
              )}
              <InfoRow label="Current Step" value={workflow.currentStep} />
            </div>
          </Card>

          {/* Annotation Coverage Warning - Show if labels were excluded or couldn't be annotated */}
          {(() => {
            const coverage = workflow.annotationResult?.annotationCoverage;
            if (!coverage) return null;

            const hasExcludedLabels = coverage.excludedLabels && coverage.excludedLabels.length > 0;
            const hasUnannotated = (coverage.unannotatedSentences ?? 0) > 0;
            const originalSentences = coverage.originalSentences ?? 0;
            const filteredSentences = coverage.filteredSentences ?? 0;
            const hasFilteredOut = originalSentences > filteredSentences;

            // Show panel if any issues exist
            if (!hasExcludedLabels && !hasUnannotated && !hasFilteredOut) return null;

            return (
              <Card className="bg-gray-900 ring-gray-800 border-amber-900/50">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-amber-400 text-lg">⚠️</span>
                  <Title className="text-amber-400">Annotation Coverage Info</Title>
                </div>

                {/* Show excluded labels info */}
                {hasExcludedLabels && coverage.excludedLabels && (
                  <div className="mb-4 p-3 bg-gray-800/50 rounded border-l-2 border-yellow-600">
                    <Text className="text-gray-300 mb-2">
                      <span className="text-yellow-400 font-medium">{coverage.excludedLabels.length} label type(s)</span> excluded
                      (no folder mapping configured):
                    </Text>
                    <div className="flex flex-wrap gap-1">
                      {coverage.excludedLabels.map((label: string) => (
                        <Badge key={label} color="yellow" size="xs">{label}</Badge>
                      ))}
                    </div>
                    {hasFilteredOut && (
                      <Text className="text-gray-500 text-xs mt-2">
                        {originalSentences - filteredSentences} sentence(s) were not annotated
                        because all their labels were excluded.
                      </Text>
                    )}
                  </div>
                )}

                {/* Show annotation stats - always show if there was filtering or original harmful content */}
                {(originalSentences > 0 || filteredSentences > 0) && (
                  <div className="space-y-3 mb-4">
                    {/* Label counts - primary metric */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-800/50 rounded p-3">
                        <div className="flex items-center">
                          <Text className="text-gray-500 text-xs">Labels Found</Text>
                          <Tooltip text={`${coverage.originalLabels ?? 0} label instances across ${coverage.originalSentences ?? 0} sentences. A single sentence can have multiple labels from different classifiers.`} />
                        </div>
                        <Text className="text-gray-200 font-semibold">
                          {coverage.originalLabels ?? 0}
                        </Text>
                        <Text className="text-gray-500 text-xs">
                          across {coverage.originalSentences ?? 0} sentences
                        </Text>
                      </div>
                      <div className="bg-gray-800/50 rounded p-3">
                        <Text className="text-gray-500 text-xs">Labels with Folder Mapping</Text>
                        <Text className={(coverage.filteredLabels ?? 0) > 0 ? "text-blue-400 font-semibold" : "text-gray-500 font-semibold"}>
                          {coverage.filteredLabels ?? 0}
                        </Text>
                      </div>
                      <div className="bg-gray-800/50 rounded p-3">
                        <Text className="text-gray-500 text-xs">Labels Annotated</Text>
                        <Text className={coverage.annotatedLabels > 0 ? "text-emerald-400 font-semibold" : "text-gray-500 font-semibold"}>
                          {coverage.annotatedLabels ?? 0}
                        </Text>
                      </div>
                    </div>
                    {/* Sentence counts - secondary info */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-800/30 rounded p-2">
                        <Text className="text-gray-600 text-xs">Sentences Found</Text>
                        <Text className="text-gray-400 text-sm">
                          {coverage.originalSentences}
                        </Text>
                      </div>
                      <div className="bg-gray-800/30 rounded p-2">
                        <Text className="text-gray-600 text-xs">Sentences with Mapping</Text>
                        <Text className="text-gray-400 text-sm">
                          {coverage.filteredSentences}
                        </Text>
                      </div>
                      <div className="bg-gray-800/30 rounded p-2">
                        <Text className="text-gray-600 text-xs">Sentences Annotated</Text>
                        <Text className="text-gray-400 text-sm">
                          {coverage.annotatedSentences}
                        </Text>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show unannotated due to missing bboxes */}
                {hasUnannotated && (
                  <div className="mb-4 p-3 bg-gray-800/50 rounded border-l-2 border-amber-600">
                    <Text className="text-gray-300 mb-2">
                      <span className="text-amber-400 font-medium">{coverage.unannotatedSentences} sentence(s)</span> could
                      not be highlighted (bounding box data unavailable).
                    </Text>
                    {/* Show unannotated sentences details */}
                    {coverage.unannotatedDetails && coverage.unannotatedDetails.length > 0 && (
                      <div className="mt-2">
                        <Text className="text-gray-500 text-xs mb-2">
                          Showing first {coverage.unannotatedDetails.length}:
                        </Text>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {coverage.unannotatedDetails.map((item: { sentence: string; sentenceIndex: number; labels: string[] }, idx: number) => (
                            <div
                              key={idx}
                              className="bg-gray-700/50 rounded p-2"
                            >
                              <Text className="text-gray-300 text-sm">"{item.sentence}"</Text>
                              <div className="flex gap-2 mt-1">
                                <Text className="text-gray-500 text-xs">#{item.sentenceIndex + 1}</Text>
                                <Text className="text-amber-400 text-xs">{item.labels.join(', ')}</Text>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })()}

          {/* Evaluation Results - Labels Found */}
          {/* Uses sentencesByLabel which groups by classifierName:label composite keys */}
          {Object.keys(sentencesByLabel).length > 0 && (
            <Card className="bg-gray-900 ring-gray-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Title className="text-gray-100">Labels Found</Title>
                  <button
                    onClick={() => documentId && exportLabelsCsv(
                      documentId,
                      workflow?.evaluationResult?.harmfulResults,
                      workflow?.extractResult?.sentenceBboxes,
                      workflow?.extractResult?.extractionResults,
                      fileName
                    )}
                    className="px-2 py-1 text-xs text-gray-400 hover:text-gray-200 bg-gray-800 hover:bg-gray-700 rounded transition-colors"
                  >
                    Export CSV
                  </button>
                </div>
                <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-0.5">
                  <button
                    onClick={() => setLabelFilter('all')}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${
                      labelFilter === 'all'
                        ? 'bg-gray-700 text-gray-100'
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    All ({Object.keys(sentencesByLabel).length})
                  </button>
                  <button
                    onClick={() => setLabelFilter('toAnnotate')}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${
                      labelFilter === 'toAnnotate'
                        ? 'bg-gray-700 text-gray-100'
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    To Annotate ({Object.keys(toAnnotateSentencesByLabel).length})
                  </button>
                  <button
                    onClick={() => setLabelFilter('notAnnotated')}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${
                      labelFilter === 'notAnnotated'
                        ? 'bg-gray-700 text-gray-100'
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    Not Annotated ({Object.keys(notAnnotatedSentencesByLabel).length})
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                {Object.entries(filteredSentencesByLabel).map(([compositeKey, sentences]) => {
                  const isExpanded = expandedLabels.has(compositeKey);
                  const hasDetails = sentences.length > 0;

                  // Parse composite key (classifierName:label or just label)
                  const [classifierName, labelName] = compositeKey.includes(':')
                    ? [compositeKey.split(':')[0], compositeKey.split(':').slice(1).join(':')]
                    : [null, compositeKey];

                  return (
                    <div key={compositeKey} className="border-b border-gray-800/50 last:border-0">
                      <button
                        onClick={() => hasDetails && toggleLabel(compositeKey)}
                        className={`w-full flex justify-between items-center py-2 px-1 rounded transition-colors ${
                          hasDetails
                            ? 'hover:bg-gray-800/50 cursor-pointer'
                            : 'cursor-default'
                        }`}
                        disabled={!hasDetails}
                      >
                        <div className="flex items-center gap-2">
                          {hasDetails && (
                            <span className="text-gray-500 text-xs w-4">
                              {isExpanded ? '▼' : '▶'}
                            </span>
                          )}
                          {sentences[0]?.backend && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              sentences[0].backend === 'phi'
                                ? 'bg-purple-900/50 text-purple-300'
                                : 'bg-blue-900/50 text-blue-300'
                            }`}>
                              {sentences[0].backend === 'phi' ? 'Phi' : 'OpenAI'}
                            </span>
                          )}
                          <Text className="text-gray-300">
                            {classifierName ? `${classifierName}:${labelName}` : labelName}
                          </Text>
                        </div>
                        <Badge color="red" size="sm">{sentences.length}</Badge>
                      </button>

                      {/* Expanded sentences */}
                      {isExpanded && sentences.length > 0 && (
                        <div className="ml-6 mb-3 space-y-2">
                          {sentences.map((item, idx) => (
                            <div
                              key={`${item.sentenceIndex}-${idx}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => jumpToResult(item.sentenceIndex)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  jumpToResult(item.sentenceIndex);
                                }
                              }}
                              title="Jump to this sentence in the Results tab"
                              className="bg-gray-800/50 rounded p-2 border-l-2 border-red-600 cursor-pointer hover:bg-gray-800 transition-colors"
                            >
                              <Text className="text-gray-300 text-sm leading-relaxed">
                                "{item.sentence}"
                              </Text>
                              <div className="flex gap-3 mt-1 items-center">
                                {item.pageNumber && (
                                  <Text className="text-blue-400 text-xs">
                                    Page {item.pageNumber}
                                  </Text>
                                )}
                                {item.confidence !== undefined && (
                                  <span
                                    className={`text-xs cursor-help ${
                                      item.confidence >= 90 ? 'text-emerald-400' :
                                      item.confidence >= 70 ? 'text-yellow-400' :
                                      'text-red-400'
                                    }`}
                                    title={`Extraction confidence: ${item.confidence}% (${item.source === 'ocr' ? 'OCR' : item.source === 'embedded' ? 'Embedded text' : 'Error'})`}
                                  >
                                    {item.confidence}%
                                  </span>
                                )}
                                <Text className="text-gray-500 text-xs">
                                  Sentence #{item.sentenceIndex + 1}
                                </Text>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-800 space-y-1">
                <div className="flex justify-between">
                  <Text className="text-gray-400">Total Labels{labelFilter === 'toAnnotate' ? ' (filtered)' : ''}</Text>
                  <Text className="text-gray-200 font-medium">
                    {Object.values(filteredSentencesByLabel).reduce((sum, arr) => sum + arr.length, 0)}
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text className="text-gray-500 text-sm">Unique Label Types</Text>
                  <Text className="text-gray-400 text-sm">{Object.keys(filteredSentencesByLabel).length}</Text>
                </div>
              </div>
            </Card>
          )}

          {/* Models Used */}
          {workflow.evaluationResult?.backendAssignments && (
            <Card className="bg-gray-900 ring-gray-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Title className="text-gray-100">Models</Title>
                  <Tooltip text="Shows which classifier models and backends were used when this document was processed. Model assignments can change between runs." />
                </div>
                {workflow.evaluationResult?.bundleKey && (
                  <a
                    href={`/bundles/${workflow.evaluationResult.bundleKey}`}
                    className="text-xs text-blue-400 hover:text-blue-300 hover:underline"
                  >
                    Bundle: {workflow.evaluationResult.bundleKey}
                  </a>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800 text-left">
                      <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Classifier</th>
                      <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Backend</th>
                      <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Version</th>
                      <th className="py-2 px-3 text-xs font-medium text-gray-500 uppercase">Model</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(workflow.evaluationResult.backendAssignments).map(([key, backend]) => {
                      const model = workflow.evaluationResult?.modelAssignments?.[key];
                      const version = workflow.evaluationResult?.versionAssignments?.[key];
                      return (
                        <tr key={key} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                          <td className="py-2 px-3">
                            <a
                              href={`/classifiers/${key}`}
                              className="text-sm font-mono text-blue-400 hover:text-blue-300 hover:underline"
                            >
                              {key}
                            </a>
                          </td>
                          <td className="py-2 px-3">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              backend === 'phi'
                                ? 'bg-purple-900/50 text-purple-300'
                                : 'bg-blue-900/50 text-blue-300'
                            }`}>
                              {backend === 'phi' ? 'Phi' : 'OpenAI'}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            {version ? (
                              <a
                                href={`/classifiers/${key}/${encodeURIComponent(version)}`}
                                className="text-xs font-mono text-blue-400 hover:text-blue-300 hover:underline"
                              >
                                {version}
                              </a>
                            ) : (
                              <span className="text-xs text-gray-600">--</span>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            {model ? (
                              <code className="text-xs font-mono text-gray-500">
                                {model.split(':').slice(-2).join(':')}
                              </code>
                            ) : (
                              <span className="text-xs text-gray-600">--</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {workflow.evaluationResult.genreModel && (
                      <tr className="border-b border-gray-800/50 hover:bg-gray-800/30">
                        <td className="py-2 px-3">
                          <a
                            href="/classifiers/genre"
                            className="text-sm font-mono text-blue-400 hover:text-blue-300 hover:underline"
                          >
                            genre
                          </a>
                        </td>
                        <td className="py-2 px-3">
                          <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-blue-900/50 text-blue-300">
                            OpenAI
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          {workflow.evaluationResult.versionAssignments?.genre ? (
                            <a
                              href={`/classifiers/genre/${encodeURIComponent(workflow.evaluationResult.versionAssignments.genre)}`}
                              className="text-xs font-mono text-blue-400 hover:text-blue-300 hover:underline"
                            >
                              {workflow.evaluationResult.versionAssignments.genre}
                            </a>
                          ) : (
                            <span className="text-xs text-gray-600">--</span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <code className="text-xs font-mono text-gray-500">
                            {workflow.evaluationResult.genreModel.split(':').slice(-2).join(':')}
                          </code>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Clean result for completed docs with no labels */}
          {workflow.status === 'completed' && (!labelCounts || Object.keys(labelCounts).length === 0) && (
            <Card className="bg-gray-900 ring-gray-800">
              <Title className="text-gray-100 mb-4">Evaluation Result</Title>
              <Badge color="emerald" size="lg">Clean - No harmful content detected</Badge>
            </Card>
          )}

          {/* Processing Info */}
          <Card className="bg-gray-900 ring-gray-800">
            <Title className="text-gray-100 mb-4">Processing</Title>
            {(() => {
              const m = workflow.metrics;
              const sumOfSteps = m
                ? (m.fetchTime || 0) + (m.extractTime || 0) + (m.evaluateTime || 0) + (m.annotateTime || 0) + (m.tagTime || 0)
                : 0;
              // Use sum of steps if available, otherwise fall back to wall clock time
              const displayTime = sumOfSteps > 0 ? sumOfSteps : m?.totalProcessingTime;

              return (
                <div className="space-y-3">
                  <InfoRow
                    label="Started"
                    value={workflow.startedAt ? new Date(workflow.startedAt).toLocaleString() : '--'}
                  />
                  <InfoRow
                    label="Completed"
                    value={workflow.finishedAt ? new Date(workflow.finishedAt).toLocaleString() : '--'}
                  />
                  <InfoRow
                    label="Total Time"
                    value={formatDuration(displayTime)}
                  />
                </div>
              );
            })()}

            {/* Step timing breakdown */}
            {workflow.metrics && (
              workflow.metrics.fetchTime ||
              workflow.metrics.extractTime ||
              workflow.metrics.evaluateTime ||
              workflow.metrics.annotateTime ||
              workflow.metrics.tagTime
            ) && (
              <div className="mt-4 pt-4 border-t border-gray-800">
                <Text className="text-gray-400 text-sm mb-3">Step Breakdown</Text>
                {(() => {
                  const m = workflow.metrics!;
                  const sumOfSteps = (m.fetchTime || 0) + (m.extractTime || 0) +
                                     (m.evaluateTime || 0) + (m.annotateTime || 0) + (m.tagTime || 0);
                  const queueWaitTime = m.totalProcessingTime && sumOfSteps > 0
                    ? Math.max(0, m.totalProcessingTime - sumOfSteps)
                    : 0;

                  return (
                    <div className="space-y-2">
                      <StepTimeRow
                        step="Fetch"
                        time={m.fetchTime}
                        total={sumOfSteps}
                      />
                      <StepTimeRow
                        step="Extract"
                        time={m.extractTime}
                        total={sumOfSteps}
                      />
                      <StepTimeRow
                        step="Evaluate"
                        time={m.evaluateTime}
                        total={sumOfSteps}
                      />
                      <StepTimeRow
                        step="Annotate"
                        time={m.annotateTime}
                        total={sumOfSteps}
                      />
                      <StepTimeRow
                        step="Tag"
                        time={m.tagTime}
                        total={sumOfSteps}
                      />
                      {queueWaitTime > 0 && (
                        <div className="pt-2 mt-2 border-t border-gray-800/50">
                          <div className="flex justify-between">
                            <Text className="text-gray-500 text-sm">Queue wait</Text>
                            <Text className="text-gray-500 text-sm">
                              {formatDuration(queueWaitTime)}
                            </Text>
                          </div>
                        </div>
                      )}
                      <div className="pt-2 mt-2 border-t border-gray-800/50">
                        <div className="flex justify-between">
                          <Text className="text-gray-400 text-sm font-medium">Total</Text>
                          <Text className="text-gray-300 text-sm font-medium">
                            {formatDuration(sumOfSteps)}
                          </Text>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </Card>

          {/* Error Info - Only show if there's an actual error */}
          {workflow.error?.message && (
            <Card className="bg-gray-900 ring-gray-800 border-red-900">
              <Title className="text-red-400 mb-4">Error</Title>
              <Text className="text-gray-300">{workflow.error.message}</Text>
              {workflow.error.step && (
                <Text className="text-gray-500 text-sm mt-2">Step: {workflow.error.step}</Text>
              )}
            </Card>
          )}
        </div>

        {/* Right column - PDF Preview */}
        <div className="lg:col-span-2">
          <Card className="bg-gray-900 ring-gray-800">
            <TabGroup index={selectedTab} onIndexChange={setSelectedTab}>
              <Flex justifyContent="between" alignItems="center" className="mb-4">
                <Title className="text-gray-100">Document Preview</Title>
                <TabList variant="solid" className="bg-gray-800 p-1 rounded-lg">
                  <Tab
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      selectedTab === 0
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                    }`}
                  >
                    Original
                  </Tab>
                  <Tab
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      selectedTab === 1
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    disabled={!annotatedFileUrl && !workflow.annotationResult?.annotatedFileName}
                  >
                    Annotated {(() => {
                      const coverage = workflow.annotationResult?.annotationCoverage;
                      if (coverage?.filteredLabels) {
                        const annotated = coverage.annotatedLabels ?? 0;
                        // Only show fraction if incomplete
                        return annotated < coverage.filteredLabels
                          ? `(${annotated}/${coverage.filteredLabels})`
                          : `(${annotated})`;
                      }
                      return workflow.annotationResult?.highlightedCount ? `(${workflow.annotationResult.highlightedCount})` : '';
                    })()}
                  </Tab>
                  <Tab
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      selectedTab === 2
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    disabled={!workflow.extractResult?.sentences?.length}
                  >
                    Results {harmfulIndices.length > 0 ? `(${harmfulIndices.length})` : ''}
                  </Tab>
                </TabList>
              </Flex>
              <TabPanels>
                {/* Original Document */}
                <TabPanel>
                  {workflow.fetchResult?.mimeType === 'application/vnd.ms-outlook' ? (
                    // MSG email viewer
                    msgPreview ? (
                      <div className="msg-viewer rounded border border-gray-800 overflow-auto">
                        {/* Email header card */}
                        <div className="bg-gray-800/60 border-b border-gray-700 px-6 py-4 space-y-2">
                          <div className="flex items-start gap-3">
                            <Text className="text-gray-400 w-16 shrink-0">Subject</Text>
                            <Text className="text-gray-100 font-semibold">{msgPreview.subject || '(no subject)'}</Text>
                          </div>
                          <div className="flex items-start gap-3">
                            <Text className="text-gray-400 w-16 shrink-0">From</Text>
                            <Text className="text-gray-200">
                              {msgPreview.senderName && msgPreview.senderEmail
                                ? `${msgPreview.senderName} <${msgPreview.senderEmail}>`
                                : msgPreview.senderName || msgPreview.senderEmail || '(unknown)'}
                            </Text>
                          </div>
                          {msgPreview.recipients.length > 0 && (
                            <div className="flex items-start gap-3">
                              <Text className="text-gray-400 w-16 shrink-0">To</Text>
                              <Text className="text-gray-200">{msgPreview.recipients.join(', ')}</Text>
                            </div>
                          )}
                          {msgPreview.sentDate && (
                            <div className="flex items-start gap-3">
                              <Text className="text-gray-400 w-16 shrink-0">Date</Text>
                              <Text className="text-gray-200">{new Date(msgPreview.sentDate).toLocaleString()}</Text>
                            </div>
                          )}
                          {msgPreview.attachmentCount > 0 && (
                            <div className="flex items-start gap-3">
                              <Text className="text-gray-400 w-16 shrink-0">Attachments</Text>
                              <Text className="text-gray-200">{msgPreview.attachments.map(a => a.name).join(', ')}</Text>
                            </div>
                          )}
                        </div>
                        {/* Email body */}
                        <div className="px-6 py-4">
                          {msgPreview.body ? (
                            <iframe
                              srcDoc={msgPreview.body}
                              sandbox="allow-same-origin"
                              className="msg-body-iframe w-full rounded bg-white"
                              title="Email body"
                              onLoad={(e) => {
                                const iframe = e.currentTarget;
                                try { iframe.style.height = iframe.contentDocument!.body.scrollHeight + 'px'; } catch {}
                              }}
                            />
                          ) : msgPreview.bodyText ? (
                            <pre className="text-gray-200 text-sm whitespace-pre-wrap font-sans">{msgPreview.bodyText}</pre>
                          ) : (
                            <Text className="text-gray-500">(no message body)</Text>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-96 bg-gray-800/50 rounded border border-gray-800">
                        <Text className="text-gray-500">
                          {msgPreviewError ? `Preview error: ${msgPreviewError}` : 'Loading message preview...'}
                        </Text>
                      </div>
                    )
                  ) : (workflow.fetchResult?.mimeType === DOCX_MIME || workflow.fetchResult?.mimeType === DOC_MIME) ? (
                    // Word rendered server-side: .docx → HTML (mammoth), .doc → text
                    // (word-extractor). Faithful content, not pixel-perfect layout.
                    docxPreview ? (
                      docxPreview.html ? (
                        <iframe
                          srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>`
                            + `body{font-family:-apple-system,system-ui,'Segoe UI',Roboto,Arial,sans-serif;color:#1a1a1a;background:#fff;margin:0;padding:32px 40px;line-height:1.5;}`
                            + `img{max-width:100%;height:auto;}`
                            + `table{border-collapse:collapse;margin:1em 0;}td,th{border:1px solid #ccc;padding:4px 8px;}`
                            + `h1,h2,h3,h4{margin:1em 0 .4em;line-height:1.25;}p{margin:0 0 .6em;}`
                            + `</style></head><body>${docxPreview.html}</body></html>`}
                          sandbox="allow-same-origin"
                          className="w-full rounded border border-gray-800 bg-white"
                          style={{ height: 'calc(100vh - 220px)', minHeight: '500px' }}
                          title="Original Document"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center gap-3 h-96 bg-gray-800/50 rounded border border-gray-800">
                          <Text className="text-gray-400">This Word document has no renderable content.</Text>
                          <Text className="text-gray-600 text-sm">Use the download icon next to the filename to view the original.</Text>
                        </div>
                      )
                    ) : (
                      <div className="flex items-center justify-center h-96 bg-gray-800/50 rounded border border-gray-800">
                        <Text className="text-gray-500">
                          {docxPreviewError ? `Preview error: ${docxPreviewError}` : 'Loading document preview...'}
                        </Text>
                      </div>
                    )
                  ) : fileUrl ? (
                    // PDF viewer
                    <div className="relative w-full" style={{ height: 'calc(100vh - 220px)', minHeight: '500px' }}>
                      <iframe
                        src={`${fileUrl}#page=1&zoom=page-width`}
                        className="w-full h-full rounded border border-gray-800"
                        title="Original Document"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-96 bg-gray-800/50 rounded border border-gray-800">
                      <Text className="text-gray-500">
                        {fileUrlError
                          ? `Preview error: ${fileUrlError}`
                          : hasFile
                          ? 'Loading preview...'
                          : (workflow.status === 'pending' || workflow.status === 'processing')
                          ? 'Document is still being processed...'
                          : 'No preview available'}
                      </Text>
                    </div>
                  )}
                </TabPanel>
                {/* Annotated PDF */}
                <TabPanel>
                  {annotatedFileUrl ? (
                    <div className="relative w-full" style={{ height: 'calc(100vh - 220px)', minHeight: '500px' }}>
                      <iframe
                        src={`${annotatedFileUrl}#page=1&zoom=page-width`}
                        className="w-full h-full rounded border border-gray-800"
                        title="Annotated Document"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-96 bg-gray-800/50 rounded border border-gray-800">
                      <Text className="text-gray-500">
                        {workflow.annotationResult?.annotatedFileName && !annotatedFileUrlError
                          ? 'Loading annotated preview...'
                          : annotatedFileUrlError
                          ? `Preview error: ${annotatedFileUrlError}`
                          : 'Annotated PDF not available yet'}
                      </Text>
                    </div>
                  )}
                </TabPanel>

                {/* Results — full extracted text with flagged sentences highlighted
                    and Prev/Next navigation between them. */}
                <TabPanel>
                  {workflow.extractResult?.sentences?.length ? (
                    <ResultsTextPanel
                      sentences={workflow.extractResult.sentences}
                      sentenceBboxes={workflow.extractResult.sentenceBboxes}
                      sentenceProvenance={workflow.extractResult.sentenceProvenance}
                      harmfulSentenceMap={harmfulSentenceMap}
                      activeSentenceIndex={activeSentenceIndex}
                      onActiveChange={setActiveSentenceIndex}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-96 bg-gray-800/50 rounded border border-gray-800">
                      <Text className="text-gray-500">No extracted text available</Text>
                    </div>
                  )}
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </Card>

          {/* Extracted Text (collapsible) — the normalized sentences sent to the
              classifier. Available for EVERY document type: PDF renders per-page
              (with OCR source/confidence), DOCX / plaintext / email / transcripts
              render a flat list. Transcripts also show printed page:line citations. */}
          {(() => {
            const extractionResults = workflow.extractResult?.extractionResults;
            const flatSentences = workflow.extractResult?.sentences;
            const provenance = workflow.extractResult?.sentenceProvenance;
            const reflowApplied = workflow.extractResult?.transcriptReflowApplied;
            const reflowConfidence = workflow.extractResult?.transcriptConfidence;
            const hasPaged = !!extractionResults && extractionResults.length > 0;
            const hasFlat = !!flatSentences && flatSentences.length > 0;
            if (!hasPaged && !hasFlat) return null;

            const totalSentences = hasPaged
              ? extractionResults!.reduce((sum, p) => sum + p.sentences.length, 0)
              : flatSentences!.length;
            const subtitle = hasPaged
              ? `${totalSentences} sentences across ${extractionResults!.length} pages`
              : `${totalSentences} sentences`;
            const provByIndex = provenance
              ? new Map(provenance.map((p) => [p.sentenceIndex, p]))
              : null;

            return (
            <Card className="bg-gray-900 ring-gray-800 mt-6">
              <button
                onClick={() => setShowExtractedText(!showExtractedText)}
                className="w-full flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Title className="text-gray-100">Extracted Text</Title>
                  <Text className="text-gray-500 text-sm">{subtitle}</Text>
                  {reflowApplied && (
                    <Badge color="violet" size="xs">
                      transcript reflow{typeof reflowConfidence === 'number' ? ` · ${Math.round(reflowConfidence * 100)}%` : ''}
                    </Badge>
                  )}
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${showExtractedText ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showExtractedText && (
                <div className="mt-4 space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
                  {hasPaged ? (
                    extractionResults!.map((page) => (
                    <div key={page.page}>
                      <div className="flex items-center gap-2 mb-2 sticky top-0 bg-gray-900 py-1 z-10">
                        <Text className="text-gray-300 font-medium text-sm">Page {page.page}</Text>
                        <Badge
                          color={page.source === 'embedded' ? 'emerald' : page.source === 'ocr' ? 'blue' : 'red'}
                          size="xs"
                        >
                          {page.source === 'embedded' ? 'Embedded' : page.source === 'ocr' ? 'OCR' : 'Error'}
                        </Badge>
                        {page.source === 'ocr' && (
                          <Badge
                            color={page.confidence >= 80 ? 'emerald' : page.confidence >= 50 ? 'yellow' : 'red'}
                            size="xs"
                          >
                            {page.confidence}% conf
                          </Badge>
                        )}
                        <Text className="text-gray-600 text-xs">{page.sentences.length} sentences</Text>
                      </div>
                      <div className="space-y-1 ml-1">
                        {page.sentences.map((sentence, i) => (
                          <div key={i} className="flex gap-2 group">
                            <Text className="text-gray-600 text-xs font-mono shrink-0 pt-0.5 select-none w-6 text-right">
                              {i + 1}
                            </Text>
                            <Text className="text-gray-400 text-sm leading-relaxed break-words">
                              {sentence}
                            </Text>
                          </div>
                        ))}
                        {page.sentences.length === 0 && (
                          <Text className="text-gray-600 text-sm italic ml-8">No text extracted from this page</Text>
                        )}
                      </div>
                    </div>
                    ))
                  ) : (
                    <div className="space-y-1 ml-1">
                      {flatSentences!.map((sentence, i) => {
                        const p = provByIndex?.get(i);
                        const cite = p && p.page != null
                          ? `p${p.page}:${p.lineStart ?? '?'}${p.lineEnd != null && p.lineEnd !== p.lineStart ? `-${p.lineEnd}` : ''}`
                          : null;
                        return (
                          <div key={i} className="flex gap-2 group">
                            <Text className="text-gray-600 text-xs font-mono shrink-0 pt-0.5 select-none w-6 text-right">
                              {i + 1}
                            </Text>
                            {cite && (
                              <Text
                                className="text-violet-400/70 text-xs font-mono shrink-0 pt-0.5 select-none whitespace-nowrap"
                                title={p?.speaker ? `speaker: ${p.speaker}` : undefined}
                              >
                                {cite}{p?.speaker ? ` ${p.speaker}` : ''}
                              </Text>
                            )}
                            <Text className="text-gray-400 text-sm leading-relaxed break-words">
                              {sentence}
                            </Text>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </Card>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

interface InfoRowProps {
  label: string;
  value: string;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex justify-between">
      <Text className="text-gray-400">{label}</Text>
      <Text className="text-gray-200">{value}</Text>
    </div>
  );
}

interface StepTimeRowProps {
  step: string;
  time: number | undefined;
  total: number | undefined;
}

function StepTimeRow({ step, time, total }: StepTimeRowProps) {
  if (!time) return null;

  const percentage = total ? Math.round((time / total) * 100) : 0;

  return (
    <div className="flex items-center gap-2">
      <Text className="text-gray-500 text-sm w-16">{step}</Text>
      <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-600 rounded-full"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <Text className="text-gray-400 text-sm w-16 text-right">
        {formatDuration(time)}
      </Text>
    </div>
  );
}
